// Agent Performance Report — one row per agent over a date range, with
// expandable day-by-day / per-campaign drill-down and CSV/Excel export.
import { useEffect, useMemo, useState } from "react";
import {
  UserCog, Loader2, RefreshCw, Download, ChevronDown, ChevronRight,
  Filter, X, FileSpreadsheet,
} from "lucide-react";
import { toast } from "sonner";
import api, { API, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover, PopoverTrigger, PopoverContent,
} from "@/components/ui/popover";

function fmtHMS(sec) {
  const s = Math.max(0, Math.round(sec || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  const ss = s % 60;
  return `${m}m ${String(ss).padStart(2, "0")}s`;
}
function fmtDT(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
}
function fmtCell(val, type) {
  if (val === null || val === undefined || val === "") return type === "text" ? "—" : "0";
  if (type === "seconds") return fmtHMS(val);
  if (type === "datetime") return fmtDT(val);
  if (type === "percent") return `${val}%`;
  if (type === "int") return Intl.NumberFormat().format(val);
  return val;
}

// ---- Compact multi-select (Popover + checkboxes) ----
function MultiSelect({ label, options, selected, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const toggle = (id) => {
    onChange(selected.includes(id)
      ? selected.filter((x) => x !== id)
      : [...selected, id]);
  };
  const count = selected.length;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5 h-9" data-testid={testid}>
          <Filter className="w-3.5 h-3.5" />
          {label}{count > 0 && <Badge className="ml-1 h-5 px-1.5 bg-primary text-primary-foreground text-[10px]">{count}</Badge>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2 max-h-72 overflow-auto" align="start">
        {count > 0 && (
          <button
            className="text-xs text-primary hover:underline mb-2 flex items-center gap-1"
            onClick={() => onChange([])} data-testid={`${testid}-clear`}>
            <X className="w-3 h-3" /> Clear ({count})
          </button>
        )}
        {options.length === 0 && <div className="text-xs text-muted-foreground px-1 py-2">No options</div>}
        <div className="space-y-1">
          {options.map((o) => (
            <label key={o.id} className="flex items-center gap-2 text-sm px-1.5 py-1 rounded hover:bg-muted/50 cursor-pointer">
              <Checkbox checked={selected.includes(o.id)} onCheckedChange={() => toggle(o.id)} data-testid={`${testid}-opt-${o.id}`} />
              <span className="truncate">{o.name}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function isoToday() { return new Date().toISOString().slice(0, 10); }

export default function AgentPerformancePage() {
  const [start, setStart] = useState(isoToday());
  const [end, setEnd] = useState(isoToday());
  const [filters, setFilters] = useState({ campaigns: [], groups: [], agents: [] });
  const [campaignIds, setCampaignIds] = useState([]);
  const [groupIds, setGroupIds] = useState([]);
  const [agentIds, setAgentIds] = useState([]);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState({}); // agent_id -> detail payload

  useEffect(() => {
    api.get("/reports/agent-performance/filters")
      .then((r) => setFilters(r.data))
      .catch((e) => toast.error(formatErr(e)));
  }, []);

  const qs = useMemo(() => {
    const p = new URLSearchParams({ start, end });
    campaignIds.forEach((c) => p.append("campaign_id", c));
    groupIds.forEach((g) => p.append("group_id", g));
    agentIds.forEach((a) => p.append("agent_id", a));
    return p.toString();
  }, [start, end, campaignIds, groupIds, agentIds]);

  async function load() {
    setBusy(true); setExpanded(null); setDetail({});
    try {
      const r = await api.get(`/reports/agent-performance?${qs}`);
      setData(r.data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function toggleRow(agentId) {
    if (expanded === agentId) { setExpanded(null); return; }
    setExpanded(agentId);
    if (!detail[agentId]) {
      try {
        const p = new URLSearchParams({ agent_id: agentId, start, end });
        campaignIds.forEach((c) => p.append("campaign_id", c));
        const r = await api.get(`/reports/agent-performance/detail?${p.toString()}`);
        setDetail((d) => ({ ...d, [agentId]: r.data }));
      } catch (e) { toast.error(formatErr(e)); }
    }
  }

  async function exportFile(fmt) {
    try {
      const r = await api.get(`/reports/agent-performance/export?fmt=${fmt}&${qs}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `agent-performance-${start}_to_${end}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) { toast.error(formatErr(e)); }
  }

  const columns = data?.columns || [];
  const rows = data?.rows || [];
  const totals = data?.totals || {};

  return (
    <div className="px-4 sm:px-6 py-6" data-testid="agent-performance-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2">
            <UserCog className="w-6 h-6 text-primary" /> Agent Performance
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Login hours, calls, talk time, state adherence, pause & omnichannel breakdown per agent.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => exportFile("csv")} disabled={!rows.length} className="gap-1.5" data-testid="ap-export-csv">
            <Download className="w-4 h-4" /> CSV
          </Button>
          <Button onClick={() => exportFile("xlsx")} disabled={!rows.length} className="gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="ap-export-xlsx">
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-3 mb-4 flex flex-wrap items-end gap-3" data-testid="ap-filters">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-muted-foreground block mb-1">From</label>
          <input type="date" value={start} max={end} onChange={(e) => setStart(e.target.value)}
            className="h-9 rounded-md border border-border bg-background px-2 text-sm" data-testid="ap-date-start" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-muted-foreground block mb-1">To</label>
          <input type="date" value={end} min={start} max={isoToday()} onChange={(e) => setEnd(e.target.value)}
            className="h-9 rounded-md border border-border bg-background px-2 text-sm" data-testid="ap-date-end" />
        </div>
        <MultiSelect label="Campaign" options={filters.campaigns} selected={campaignIds} onChange={setCampaignIds} testid="ap-filter-campaign" />
        <MultiSelect label="User Group" options={filters.groups} selected={groupIds} onChange={setGroupIds} testid="ap-filter-group" />
        <MultiSelect label="Agent" options={filters.agents} selected={agentIds} onChange={setAgentIds} testid="ap-filter-agent" />
        <Button onClick={load} disabled={busy} className="gap-1.5 h-9" data-testid="ap-apply">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Apply
        </Button>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden" data-testid="ap-table-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead className="bg-muted/50 sticky top-0">
              <tr>
                <th className="w-8 px-2 py-2"></th>
                {columns.map((c, i) => (
                  <th key={c.key} className={`px-3 py-2 font-semibold text-[11px] uppercase tracking-wider ${i < 2 ? "text-left" : "text-right"}`}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {busy && (
                <tr><td colSpan={columns.length + 1} className="px-3 py-10 text-center text-muted-foreground">
                  <Loader2 className="w-5 h-5 animate-spin inline" /> Loading…
                </td></tr>
              )}
              {!busy && rows.length === 0 && (
                <tr><td colSpan={columns.length + 1} className="px-3 py-10 text-center text-muted-foreground italic">
                  No agent activity for these filters.
                </td></tr>
              )}
              {!busy && rows.map((r) => (
                <RowGroup key={r.agent_id} row={r} columns={columns}
                  expanded={expanded === r.agent_id}
                  onToggle={() => toggleRow(r.agent_id)}
                  detail={detail[r.agent_id]} />
              ))}
            </tbody>
            {!busy && rows.length > 0 && (
              <tfoot>
                <tr className="bg-muted/60 font-bold border-t-2 border-border" data-testid="ap-totals-row">
                  <td className="px-2 py-2"></td>
                  {columns.map((c, i) => (
                    <td key={c.key} className={`px-3 py-2 ${i < 2 ? "text-left" : "text-right font-mono"}`}>
                      {c.key === "agent_name" ? `TOTAL (${totals.agents || 0})`
                        : c.key === "email" ? ""
                        : ["first_login", "last_logout", "campaigns"].includes(c.key) ? ""
                        : fmtCell(totals[c.key], c.type)}
                    </td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>
    </div>
  );
}

function RowGroup({ row, columns, expanded, onToggle, detail }) {
  return (
    <>
      <tr className="border-t border-border/60 hover:bg-muted/20 cursor-pointer" onClick={onToggle} data-testid={`ap-row-${row.agent_id}`}>
        <td className="px-2 py-2 text-muted-foreground">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </td>
        {columns.map((c, i) => (
          <td key={c.key} className={`px-3 py-2 ${i < 2 ? "text-left" : "text-right font-mono"} ${c.key === "agent_name" ? "font-medium" : ""}`}>
            {fmtCell(row[c.key], c.type)}
          </td>
        ))}
      </tr>
      {expanded && (
        <tr className="bg-muted/10">
          <td colSpan={columns.length + 1} className="px-4 py-3">
            <DrillDown row={row} detail={detail} />
          </td>
        </tr>
      )}
    </>
  );
}

function DrillDown({ row, detail }) {
  const pause = row.pause_breakdown || {};
  const channels = row.channel_breakdown || {};
  const disps = row.disposition_breakdown || {};
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid={`ap-drill-${row.agent_id}`}>
      {/* Pause + channel + disposition breakdown */}
      <div className="space-y-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">Dispositions Captured</div>
          {Object.keys(disps).length === 0 ? <div className="text-xs text-muted-foreground italic">No dispositions recorded.</div> : (
            <div className="flex flex-wrap gap-1.5" data-testid={`ap-disp-${row.agent_id}`}>
              {Object.entries(disps).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                <Badge key={k} variant="outline" className="text-xs">{k}: {v}</Badge>
              ))}
            </div>
          )}
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">Pause Breakdown</div>
          {Object.keys(pause).length === 0 ? <div className="text-xs text-muted-foreground italic">No pauses recorded.</div> : (
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(pause).map(([k, v]) => (
                <Badge key={k} variant="outline" className="text-xs">{k}: {fmtHMS(v)}</Badge>
              ))}
            </div>
          )}
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">Omnichannel Time</div>
          {Object.keys(channels).length === 0 ? <div className="text-xs text-muted-foreground italic">No digital activity recorded.</div> : (
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(channels).map(([k, v]) => (
                <Badge key={k} variant="outline" className="text-xs capitalize">{k}: {fmtHMS(v)}</Badge>
              ))}
            </div>
          )}
        </div>
        {detail?.campaigns?.length > 0 && (
          <div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">By Campaign</div>
            <table className="w-full text-xs">
              <thead><tr className="text-muted-foreground">
                <th className="text-left py-1">Campaign</th><th className="text-right">Calls</th>
                <th className="text-right">Conn.</th><th className="text-right">Avg Talk</th>
              </tr></thead>
              <tbody>
                {detail.campaigns.map((c, i) => (
                  <tr key={i} className="border-t border-border/40">
                    <td className="py-1">{c.campaign}</td>
                    <td className="text-right font-mono">{c.total_calls}</td>
                    <td className="text-right font-mono">{c.connected}</td>
                    <td className="text-right font-mono">{fmtHMS(c.avg_talk_seconds)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Day-by-day */}
      <div>
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">Day-by-Day</div>
        {!detail ? <div className="text-xs text-muted-foreground"><Loader2 className="w-3.5 h-3.5 animate-spin inline" /> Loading…</div>
          : detail.days?.length === 0 ? <div className="text-xs text-muted-foreground italic">No daily data.</div> : (
          <div className="max-h-56 overflow-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-card"><tr className="text-muted-foreground">
                <th className="text-left py-1">Day</th><th className="text-right">Calls</th>
                <th className="text-right">Conn.</th><th className="text-right">Talk</th>
                <th className="text-right">Ready</th><th className="text-right">Wrap</th>
                <th className="text-right">Digital</th>
              </tr></thead>
              <tbody>
                {detail.days.map((d) => (
                  <tr key={d.day} className="border-t border-border/40">
                    <td className="py-1">{d.day}</td>
                    <td className="text-right font-mono">{d.total_calls}</td>
                    <td className="text-right font-mono">{d.connected}</td>
                    <td className="text-right font-mono">{fmtHMS(d.talk_seconds)}</td>
                    <td className="text-right font-mono">{fmtHMS(d.ready_seconds)}</td>
                    <td className="text-right font-mono">{fmtHMS(d.wrap_seconds)}</td>
                    <td className="text-right font-mono">{fmtHMS(d.digital_seconds)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
